import ApiService from './api.service';

export interface DemoConfigDisplay {
  client_name: string;
  logo_url: string | null;
  brand_color: string | null;
}

export interface DemoSessionStart {
  url: string;
  expires_at: number;
  jti: string;
}

export interface DemoConfigChatSettings {
  model?: string | null;
  verbosity?: string | null;
  reasoning?: string | null;
  max_output_tokens?: number | null;
}

export interface DemoConfigChatRenderConfig {
  center_input_on_new_chat: boolean;
  placeholder: string;
  welcome_message: string;
}

export interface DemoConfig {
  id: string;
  client_name: string;
  logo_url?: string | null;
  brand_color?: string | null;
  tenant_uuid: string;
  branch: 'PROD' | 'DRAFT';
  dimension_filters: Record<string, string[]>;
  blacklisted_metrics: string[];
  chat_settings: DemoConfigChatSettings;
  show_history: boolean;
  chat_render_config: DemoConfigChatRenderConfig;
  expires_at: string;
  enabled: boolean;
  created_by: string;
  created_at: string;
}

export type SingleRecordResponse<T> = { record: T };
export type ListRecordsResponse<T> = {
  records: T[];
  total_records?: number;
  total_pages?: number;
};

export const demoConfigService = {
  // Public, unauthenticated — backs the /demo/:id landing page.
  async getDemoConfig(id: string): Promise<DemoConfigDisplay> {
    return (await ApiService.getInstance().request(
      'GET',
      `/ext/demo-config/${id}`
    )) as DemoConfigDisplay;
  },

  async startDemoSession(id: string): Promise<DemoSessionStart> {
    return (await ApiService.getInstance().request(
      'POST',
      `/ext/demo-config/${id}/start`
    )) as DemoSessionStart;
  },

  // Admin CRUD — backs the /demo-configs management page. tenant_uuid is
  // forced server-side to the caller's own tenant on create (see
  // DemoConfigResource); it's read-only here, never sent in the body.
  async find(options?: {
    page?: number;
    pageSize?: number;
  }): Promise<ListRecordsResponse<DemoConfig>> {
    const { page = 1, pageSize = 100 } = options || {};
    return (await ApiService.getInstance().request('GET', '/demo-configs', {
      page,
      limit: pageSize,
    })) as ListRecordsResponse<DemoConfig>;
  },

  async findById(id: string): Promise<DemoConfig> {
    const result = (await ApiService.getInstance().request(
      'GET',
      `/demo-configs/${id}`
    )) as SingleRecordResponse<DemoConfig>;
    return result.record;
  },

  async create(config: Partial<DemoConfig>): Promise<DemoConfig> {
    const result = (await ApiService.getInstance().request(
      'POST',
      '/demo-configs',
      config
    )) as SingleRecordResponse<DemoConfig>;
    return result.record;
  },

  async save(config: Partial<DemoConfig> & { id: string }): Promise<DemoConfig> {
    const { id, ...rest } = config;
    const result = (await ApiService.getInstance().request(
      'PATCH',
      `/demo-configs/${id}`,
      rest
    )) as SingleRecordResponse<DemoConfig>;
    return result.record;
  },

  async delete(id: string): Promise<void> {
    await ApiService.getInstance().request('DELETE', `/demo-configs/${id}`);
  },
};
